/**
 * What the app tells the server about the phone, for one account per device
 * (server: modules/auth/devices.ts). Three things:
 *
 *  1. The device code. A Telegram Mini App gets no hardware id, so the app
 *     makes one: random, created the first time the app opens on the phone,
 *     kept in FIVE places — the WebView's storage, a cookie, IndexedDB, the
 *     Cache API and Telegram's own DeviceStorage. Every Telegram account on
 *     the phone shares the first four. Wiping one place is not enough: the
 *     code is read back from any that still has it and written again into the
 *     rest; codes found that disagree all go to the server as the same phone.
 *  2. The hardware print. What a wiped storage or a cloned Telegram cannot
 *     change: graphics chip and its limits, a drawn canvas, an audio render,
 *     screen, cores, memory, model and system, time zone, languages. The
 *     server only treats it as the same phone together with the same network.
 *  3. Signs of an emulator or an automated browser.
 *
 * Nothing here identifies the person: the code is random and the print is a
 * hash of the hardware. Both are hashed again on the server.
 */

const KEY = 'pxc_device';
const VALID = /^[A-Za-z0-9_-]{8,64}$/;
const DB_NAME = 'pxc_device';
const CACHE_NAME = 'pxc-device';
const CACHE_URL = '/__pxc_device__';

export interface DeviceInfo {
  id: string;
  /** Other codes found on this phone (a store that kept an older one). */
  aliases: string[];
  /** Hex hash of the hardware print, or '' when it could not be read. */
  print: string;
  /** Signs of an emulator or an automated browser. */
  flags: string[];
  platform: string;
}

type TelegramDeviceStorage = {
  getItem(key: string, callback: (error: unknown, value?: string | null) => void): void;
  setItem(key: string, value: string, callback?: (error: unknown, ok?: boolean) => void): void;
};

function telegramApp(): { platform?: string; DeviceStorage?: TelegramDeviceStorage } | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { Telegram?: { WebApp?: { platform?: string; DeviceStorage?: TelegramDeviceStorage } } }).Telegram?.WebApp;
}

/** Which Telegram the app runs in (android, ios, tdesktop, web…). Only a phone's second account is banned. */
export const DEVICE_PLATFORM: string = telegramApp()?.platform ?? 'unknown';

// ---- the five stores ---------------------------------------------------------

function withTimeout<T>(work: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([work.catch(() => fallback), new Promise<T>((resolve) => window.setTimeout(() => resolve(fallback), ms))]);
}

function readLocal(): string {
  try {
    return window.localStorage.getItem(KEY) ?? '';
  } catch {
    return '';
  }
}

function writeLocal(value: string): void {
  try {
    window.localStorage.setItem(KEY, value);
  } catch {
    // Private mode or storage off: the other stores keep it.
  }
}

function readCookie(): string {
  try {
    const match = document.cookie.match(/(?:^|;\s*)pxc_device=([^;]*)/);
    return match?.[1] ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

function writeCookie(value: string): void {
  try {
    // SameSite=None and Secure: the app runs framed inside Telegram, and without
    // them the WebView drops the cookie.
    document.cookie = `${KEY}=${encodeURIComponent(value)}; path=/; max-age=34560000; SameSite=None; Secure`;
  } catch {
    // Cookies blocked: the other stores keep it.
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore('kv');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readDb(): Promise<string> {
  if (typeof window === 'undefined' || !window.indexedDB) return '';
  const db = await openDb();
  return await new Promise<string>((resolve) => {
    const get = db.transaction('kv', 'readonly').objectStore('kv').get(KEY);
    get.onsuccess = () => resolve(typeof get.result === 'string' ? get.result : '');
    get.onerror = () => resolve('');
  });
}

async function writeDb(value: string): Promise<void> {
  if (typeof window === 'undefined' || !window.indexedDB) return;
  const db = await openDb();
  db.transaction('kv', 'readwrite').objectStore('kv').put(value, KEY);
}

async function readCache(): Promise<string> {
  if (typeof caches === 'undefined') return '';
  const response = await (await caches.open(CACHE_NAME)).match(CACHE_URL);
  return response ? (await response.text()).trim() : '';
}

async function writeCache(value: string): Promise<void> {
  if (typeof caches === 'undefined') return;
  await (await caches.open(CACHE_NAME)).put(CACHE_URL, new Response(value));
}

function readTelegram(): Promise<string> {
  const storage = telegramApp()?.DeviceStorage;
  if (!storage) return Promise.resolve('');
  return new Promise((resolve) => {
    try {
      storage.getItem(KEY, (error, value) => resolve(!error && typeof value === 'string' ? value : ''));
    } catch {
      resolve('');
    }
  });
}

function writeTelegram(value: string): void {
  try {
    telegramApp()?.DeviceStorage?.setItem(KEY, value);
  } catch {
    // Older Telegram without DeviceStorage.
  }
}

function newId(): string {
  try {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    // No crypto at all (no current WebView lacks it): time, page clock and two
    // random draws, so even this fallback does not repeat between phones.
    const random = () => Math.random().toString(16).slice(2, 12);
    return `fb-${Date.now().toString(16)}-${Math.floor(performance.now() * 1000).toString(16)}-${random()}${random()}`;
  }
}

/** The code as known straight away (the fast stores): every request carries at least this. */
function quickId(): string {
  if (typeof window === 'undefined') return '';
  const found = [readLocal(), readCookie()].find((value) => VALID.test(value));
  const id = found ?? newId();
  writeLocal(id);
  writeCookie(id);
  return id;
}

export const DEVICE_ID = quickId();

/** All five stores read; the code most of them hold wins and is written back into all. */
async function resolveCode(): Promise<{ id: string; aliases: string[] }> {
  const [db, cache, telegram] = await Promise.all([
    withTimeout(readDb(), 800, ''),
    withTimeout(readCache(), 800, ''),
    withTimeout(readTelegram(), 800, ''),
  ]);
  // Order matters on a tie: the stores a wipe reaches last come first.
  const found = [telegram, db, cache, readCookie(), readLocal()].filter((value) => VALID.test(value));
  const counts = new Map<string, number>();
  for (const value of found) counts.set(value, (counts.get(value) ?? 0) + 1);
  let id = DEVICE_ID;
  let best = 0;
  for (const value of found) {
    const count = counts.get(value) ?? 0;
    if (count > best) {
      best = count;
      id = value;
    }
  }
  writeLocal(id);
  writeCookie(id);
  void writeDb(id).catch(() => undefined);
  void writeCache(id).catch(() => undefined);
  writeTelegram(id);
  return { id, aliases: [...counts.keys()].filter((value) => value !== id).slice(0, 4) };
}

// ---- the hardware print ------------------------------------------------------

function webgl(): { parts: string[]; renderer: string } {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return { parts: ['no-webgl'], renderer: '' };
    const debug = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = String(debug ? gl.getParameter(debug.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR));
    const renderer = String(debug ? gl.getParameter(debug.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    const parts = [
      vendor,
      renderer,
      String(gl.getParameter(gl.MAX_TEXTURE_SIZE)),
      String(gl.getParameter(gl.MAX_RENDERBUFFER_SIZE)),
      String(Array.from(gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array)),
      String(Array.from(gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE) as Float32Array)),
      String(gl.getParameter(gl.SHADING_LANGUAGE_VERSION)),
      (gl.getSupportedExtensions() ?? []).slice().sort().join(','),
    ];
    return { parts, renderer };
  } catch {
    return { parts: ['webgl-error'], renderer: '' };
  }
}

function canvasPrint(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 60;
    const g = canvas.getContext('2d');
    if (!g) return 'no-canvas';
    g.textBaseline = 'top';
    g.font = "16px 'Arial'";
    g.fillStyle = '#f60';
    g.fillRect(100, 1, 62, 20);
    g.fillStyle = '#069';
    g.fillText('CraftGem \u26CF 1.0 <canvas>', 2, 15);
    g.fillStyle = 'rgba(102, 204, 0, 0.7)';
    g.fillText('CraftGem \u26CF 1.0 <canvas>', 4, 17);
    g.globalCompositeOperation = 'multiply';
    for (const [x, color] of [[40, '#f2f'], [80, '#2ff'], [60, '#ff2']] as const) {
      g.fillStyle = color;
      g.beginPath();
      g.arc(x, 40, 18, 0, Math.PI * 2);
      g.fill();
    }
    return canvas.toDataURL();
  } catch {
    return 'canvas-error';
  }
}

async function audioPrint(): Promise<string> {
  try {
    const Offline =
      window.OfflineAudioContext ??
      (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
    if (!Offline) return 'no-audio';
    const context = new Offline(1, 5000, 44100);
    const oscillator = context.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.value = 10000;
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -50;
    compressor.knee.value = 40;
    compressor.ratio.value = 12;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;
    oscillator.connect(compressor);
    compressor.connect(context.destination);
    oscillator.start(0);
    const buffer = await context.startRendering();
    const data = buffer.getChannelData(0);
    let sum = 0;
    for (let index = 4500; index < 5000; index += 1) sum += Math.abs(data[index] ?? 0);
    return sum.toFixed(7);
  } catch {
    return 'audio-error';
  }
}

/** Model and system from the user agent, without the browser's version (which moves with every update). */
function modelAndSystem(): string {
  const ua = navigator.userAgent;
  const android = /Android\s+([\d.]+);\s*([^;)]*)/.exec(ua);
  if (android) return `android ${android[1]?.split('.')[0] ?? ''} ${(android[2] ?? '').replace(/\s*Build\/.*$/, '').trim()}`;
  const ios = /(iPhone|iPad|iPod).*?OS (\d+)_/.exec(ua);
  if (ios) return `ios ${ios[2] ?? ''} ${ios[1] ?? ''}`;
  return navigator.platform || 'other';
}

async function sha256(text: string): Promise<string> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    // No SubtleCrypto: a shorter hash, still the same for the same hardware.
    let a = 5381;
    let b = 52711;
    for (let index = 0; index < text.length; index += 1) {
      const code = text.charCodeAt(index);
      a = ((a << 5) + a + code) >>> 0;
      b = ((b << 5) + b + code * 31) >>> 0;
    }
    return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
  }
}

async function hardwarePrint(): Promise<{ print: string; flags: string[] }> {
  const gl = webgl();
  const nav = navigator as Navigator & { deviceMemory?: number };
  const parts = [
    ...gl.parts,
    await sha256(canvasPrint()),
    await withTimeout(audioPrint(), 1500, 'audio-timeout'),
    `${screen.width}x${screen.height}@${window.devicePixelRatio}`,
    String(screen.colorDepth),
    String(nav.hardwareConcurrency ?? ''),
    String(nav.deviceMemory ?? ''),
    String(nav.maxTouchPoints ?? ''),
    modelAndSystem(),
    Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
    (navigator.languages ?? [navigator.language]).join(','),
    DEVICE_PLATFORM,
  ];
  const flags: string[] = [];
  if (/swiftshader|llvmpipe|android emulator|virtualbox|vmware|bluestacks|genymotion|nox|ldplayer|memu/i.test(gl.renderer)) flags.push('emulator-gpu');
  if (/sdk_gphone|android sdk built for|emulator|google_sdk|generic_x86|vbox86p|ldplayer|bluestacks|nox/i.test(navigator.userAgent)) flags.push('emulator-ua');
  if ((navigator as Navigator & { webdriver?: boolean }).webdriver) flags.push('webdriver');
  return { print: await sha256(parts.join('|')), flags };
}

// ---- all of it, once per load ---------------------------------------------------

let resolved: DeviceInfo | null = null;
let pending: Promise<DeviceInfo> | null = null;

/** The phone as the server should know it. Read once per load; sign-in waits for it. */
export function deviceReady(): Promise<DeviceInfo> {
  if (resolved) return Promise.resolve(resolved);
  pending ??= (async () => {
    const fallback: DeviceInfo = { id: DEVICE_ID, aliases: [], print: '', flags: [], platform: DEVICE_PLATFORM };
    try {
      const [code, hardware] = await Promise.all([resolveCode(), withTimeout(hardwarePrint(), 2500, { print: '', flags: [] })]);
      resolved = { id: code.id, aliases: code.aliases, print: hardware.print, flags: hardware.flags, platform: DEVICE_PLATFORM };
    } catch {
      resolved = fallback;
    }
    return resolved;
  })();
  return pending;
}

/** What goes with each request: the code always, the rest once it is known. */
export function deviceHeaders(): Record<string, string> {
  const info = resolved;
  const headers: Record<string, string> = { 'x-device-uuid': info?.id ?? DEVICE_ID, 'x-device-platform': DEVICE_PLATFORM };
  if (info?.aliases.length) headers['x-device-aliases'] = info.aliases.join(',');
  if (info?.print) headers['x-device-print'] = info.print;
  if (info?.flags.length) headers['x-device-flags'] = info.flags.join(',');
  return headers;
}
